import type { Player } from '../types/game';
import { gameRules } from './gameRules';
import { calculateOvertaking, OVERTAKE_ACTUAL } from './overtake';

export type ZoneKind = 'bonus' | 'penalty';
export type ZoneTier = 1 | 2 | 3;

/**
 * Ukryty odcinek trasy dający premię albo karę. Granice są domknięte obustronnie:
 * strefa [100, 105] łapie wynik 100 oraz 105.
 */
export interface TrackZone {
  id: string;
  kind: ZoneKind;
  tier: ZoneTier;
  start: number; // inclusive
  end: number; // inclusive
  points: number; // magnituda efektu, znak wynika z `kind`
  revealed: boolean;
}

const ZONE_KINDS: readonly ZoneKind[] = ['bonus', 'penalty'];

export function snapToMultiple(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function shuffled<T>(items: readonly T[], rng: () => number): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function intersectsTunnel(start: number, end: number): boolean {
  return gameRules.tunnels.some((tunnel) => start <= tunnel.max && end >= tunnel.min);
}

/**
 * Losuje pozycje sześciu stref (po trzy każdego rodzaju). Wartości i szerokości są stałe –
 * losowana jest wyłącznie pozycja.
 *
 * Kandydaci są wyliczani wprost, a nie losowani metodą prób i błędów, więc funkcja zawsze
 * się kończy. Gdy dla którejś strefy zabraknie legalnego miejsca (bardzo ciasna konfiguracja
 * przy `avoidTunnels`), strefa jest pomijana i gra toczy się z mniejszą ich liczbą.
 */
export function placeZones(rng: () => number = Math.random, avoidTunnels: boolean = false): TrackZone[] {
  const { tiers, snap, minStart, maxEnd, minGap } = gameRules.zones;
  const specs = shuffled(
    ZONE_KINDS.flatMap((kind) => tiers.map((tier) => ({ kind, ...tier }))),
    rng,
  );

  const placed: TrackZone[] = [];
  for (const spec of specs) {
    const candidates: number[] = [];
    for (let start = minStart; start + spec.width <= maxEnd; start += snap) {
      const end = start + spec.width;
      if (avoidTunnels && intersectsTunnel(start, end)) continue;
      const conflicts = placed.some((zone) => start - zone.end < minGap && zone.start - end < minGap);
      if (conflicts) continue;
      candidates.push(start);
    }
    if (candidates.length === 0) continue;

    const start = candidates[Math.floor(rng() * candidates.length)];
    placed.push({
      id: `z-${spec.kind}-${start}`,
      kind: spec.kind,
      tier: spec.tier,
      start,
      end: start + spec.width,
      points: spec.points,
      revealed: false,
    });
  }

  return placed.sort((a, b) => a.start - b.start);
}

/**
 * Zwraca strefę, w której zakresie leży wynik. Strefy nigdy się nie nakładają,
 * więc trafienie jest co najwyżej jedno.
 */
export function findZoneAt(zones: TrackZone[] | undefined, score: number): TrackZone | null {
  if (!zones || zones.length === 0) return null;
  return zones.find((zone) => score >= zone.start && score <= zone.end) ?? null;
}

export interface ZoneChainLink {
  zone: TrackZone;
  triggerScore: number; // wynik, który wpadł w strefę
  zoneDelta: number; // efekt samej strefy, po przycięciu do niezmienników
  overtakeBonus: number; // bonus z wyprzedzeń wywołanych TYM skokiem
  penalties: { playerId: string; delta: number }[]; // ofiary tych wyprzedzeń
  resultingScore: number; // triggerScore + zoneDelta + overtakeBonus
}

export interface ZoneChainResult {
  /** Cała przebyta trasa – łącznie z ogniwami unieważnionymi przez pętlę. */
  links: ZoneChainLink[];
  /** Liczą się wyłącznie `links[0..settledIndex-1]`; reszta jest tylko do pokazania. */
  settledIndex: number;
  finalScore: number;
  /** Zakres ogniw tworzących cykl, gdy wykryto pętlę. */
  loop: { startIndex: number; endIndex: number } | null;
  /** Zbiorczy efekt zaliczonych ogniw – suma zgadza się z `finalScore`. */
  zoneDelta: number;
  overtakeBonus: number;
  penalties: { playerId: string; delta: number }[];
  /** Wszystkie odwiedzone strefy, także te z unieważnionej części pętli. */
  visitedZones: TrackZone[];
  /** Łańcuch ucięty twardym limitem, a nie naturalnie – wyłącznie do logowania. */
  hitLinkLimit: boolean;
}

export interface ZoneChainParams {
  zones: TrackZone[] | undefined;
  players: Player[];
  playerId: string;
  startScore: number;
  hasFirstEntry: boolean;
  /** Gracze wyprzedzeni już wcześniej w tej turze – nie mogą oberwać drugi raz. */
  excludedVictimIds?: Iterable<string>;
}

/**
 * Rozstrzyga cały łańcuch stref dla wyniku spoczynkowego gracza: każde wejście w strefę
 * wywołuje efekt, a wynik po efekcie (razem z bonusem z wyprzedzeń) jest sprawdzany
 * ponownie. Zwraca `null`, gdy nie odpaliło ani jedno ogniwo.
 *
 * Niezmienniki na każdym ogniwie: wynik nigdy powyżej 1000 (I1 – ogniwo, które by to
 * złamało, po prostu się nie wykonuje i łańcuch się kończy), nigdy poniżej zera (I2)
 * ani poniżej progu pierwszego wejścia dla gracza, który już wszedł do gry (I3).
 *
 * Pętla: trasa jest w pełni deterministyczna (wynik → strefa → wynik), więc powtórzony
 * wynik jest DOWODEM cyklu i wyznacza jego granice co do ogniwa. Gracz zatrzymuje się
 * wtedy na najgorszym kroku cyklu, a ogniwa po nim tracą ważność razem z wyprzedzeniami,
 * które wywołały. Minimum liczymy wyłącznie po wynikach z cyklu – prefiks prowadzący do
 * pętli jest w pełni legalny i cofanie go byłoby karą za coś, co się wydarzyło naprawdę.
 */
export function resolveZoneChain({
  zones,
  players,
  playerId,
  startScore,
  hasFirstEntry,
  excludedVictimIds,
}: ZoneChainParams): ZoneChainResult | null {
  if (!zones || zones.length === 0) return null;
  const working = players.map((player) => ({ ...player }));
  const selfIndex = working.findIndex((player) => player.id === playerId);
  if (selfIndex === -1) return null;

  const links: ZoneChainLink[] = [];
  const victimIds = new Set<string>(excludedVictimIds ?? []);
  // Wynik → indeks ogniwa, które z niego wystartowało.
  const seenScores = new Map<number, number>([[startScore, 0]]);
  let score = startScore;
  let loop: { startIndex: number; endIndex: number } | null = null;
  let hitLinkLimit = false;

  for (;;) {
    if (links.length >= gameRules.zones.maxChainLinks) {
      hitLinkLimit = true;
      break;
    }
    const zone = findZoneAt(zones, score);
    if (!zone) break;

    const rawDelta = zone.kind === 'bonus' ? zone.points : -zone.points;
    let afterZone = score + rawDelta;
    // I1 – ogniwo wypychające poza metę nie wykonuje się wcale; łańcuch kończy się przed nim.
    if (afterZone > gameRules.scoring.winScore) break;
    // I2 / I3 – kara nie schodzi poniżej zera ani nie cofa gracza sprzed pierwszego wejścia.
    const floor = hasFirstEntry ? gameRules.scoring.firstEntryMin : 0;
    if (afterZone < floor) afterZone = floor;
    const zoneDelta = afterZone - score;
    if (zoneDelta === 0) break;

    // Wyprzedzenia liczymy PER OGNIWO, z wyniku sprzed tego skoku – dlatego własny
    // wynik w roboczej tablicy musi być bieżący, a nie ten sprzed całej tury.
    working[selfIndex] = { ...working[selfIndex], score };
    const overtake = score >= gameRules.overtake.protectedScore
      ? null
      : calculateOvertaking(working, playerId, afterZone, {
        ...OVERTAKE_ACTUAL,
        excludeVictimIds: victimIds,
      });
    let overtakeBonus = overtake?.bonusPoints ?? 0;
    let victims = overtake?.overtaken ?? [];
    // I1 – w razie kolizji z metą znika bonus z wyprzedzeń, nie efekt strefy.
    if (afterZone + overtakeBonus > gameRules.scoring.winScore) {
      overtakeBonus = 0;
      victims = [];
    }
    const penaltyPoints = overtake?.penaltyPoints ?? 0;
    victims.forEach((victim) => {
      victimIds.add(victim.id);
      const victimIndex = working.findIndex((player) => player.id === victim.id);
      if (victimIndex !== -1) {
        working[victimIndex] = {
          ...working[victimIndex],
          score: working[victimIndex].score + penaltyPoints,
        };
      }
    });

    const resultingScore = afterZone + overtakeBonus;
    links.push({
      zone,
      triggerScore: score,
      zoneDelta,
      overtakeBonus,
      penalties: victims.map((victim) => ({ playerId: victim.id, delta: penaltyPoints })),
      resultingScore,
    });
    score = resultingScore;

    const seenAt = seenScores.get(score);
    if (seenAt !== undefined) {
      loop = { startIndex: seenAt, endIndex: links.length - 1 };
      break;
    }
    seenScores.set(score, links.length);
  }

  if (links.length === 0) return null;

  let settledIndex = links.length;
  if (loop) {
    let worstIndex = loop.startIndex;
    for (let i = loop.startIndex + 1; i <= loop.endIndex; i += 1) {
      if (links[i].triggerScore < links[worstIndex].triggerScore) worstIndex = i;
    }
    settledIndex = worstIndex;
  }

  const applied = links.slice(0, settledIndex);
  const visitedZones: TrackZone[] = [];
  links.forEach((link) => {
    if (!visitedZones.some((zone) => zone.id === link.zone.id)) visitedZones.push(link.zone);
  });

  return {
    links,
    settledIndex,
    finalScore: settledIndex === 0 ? startScore : links[settledIndex - 1].resultingScore,
    loop,
    zoneDelta: applied.reduce((sum, link) => sum + link.zoneDelta, 0),
    overtakeBonus: applied.reduce((sum, link) => sum + link.overtakeBonus, 0),
    penalties: applied.flatMap((link) => link.penalties),
    visitedZones,
    hitLinkLimit,
  };
}

/**
 * Odkrywa strefę w liście. Gdy strefy nie ma (klient ONLINE nigdy nie dostał tajnej mapy),
 * dopisuje ją jako odkrytą – jedna ścieżka kodu dla serwera, hosta i pozostałych klientów.
 */
export function revealZone(zones: TrackZone[] | undefined, zone: TrackZone): TrackZone[] {
  const current = zones ?? [];
  const known = current.some((candidate) => candidate.id === zone.id);
  const next = known
    ? current.map((candidate) => (candidate.id === zone.id ? { ...candidate, revealed: true } : candidate))
    : [...current, { ...zone, revealed: true }];
  return next.sort((a, b) => a.start - b.start);
}
